import React, { useCallback, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";

const rowNumberBase = {
  color: "text.secondary",
  fontWeight: 500,
  fontVariantNumeric: "tabular-nums",
  textAlign: "center",
  userSelect: "none",
};

const styles = {
  rowHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
    position: "relative",
  },
  rowNumber: rowNumberBase,
  invisibleCellCheckbox: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    m: 0,
    p: 0,
    zIndex: 2,
    opacity: 0,
    "& .MuiSvgIcon-root": { display: "none" },
  },
  selectedRowBackground: {
    position: "absolute",
    inset: 0,
    bgcolor: (theme) => alpha(theme.palette.primary.main, 1),
    zIndex: 0,
  },
  rowClickTarget: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1,
    cursor: "pointer",
    "@media (hover: hover)": {
      "&:hover .scrubber-row-number": { opacity: 0 },
      "&:hover .scrubber-row-checkbox": { opacity: 1 },
    },
  },
  hybridRowNumber: (isSelected) => ({
    ...rowNumberBase,
    position: "absolute",
    opacity: isSelected ? 0 : 1,
    transition: "opacity 150ms ease-in-out",
  }),
  hybridRowCheckbox: (isSelected) => ({
    position: "absolute",
    opacity: isSelected ? 1 : 0,
    transition: "opacity 150ms ease-in-out",
    p: 0,
    pointerEvents: "none",
    color: isSelected ? "white" : "action.active",
    "&.Mui-checked": { color: "white" },
  }),
};

const SelectAllCell = () => (
  <Box
    sx={{
      position: "absolute",
      inset: 0,
      bgcolor: "white",
      borderRight: "1px solid #c0c0c0",
      borderBottom: "1px solid #c0c0c0",
    }}
  >
    <Box
      sx={{
        position: "absolute",
        right: 0,
        bottom: 0,
        width: 0,
        height: 0,
        borderStyle: "solid",
        borderWidth: "0 0 55px 55px",
        borderColor: "transparent transparent #ECEDEE transparent",
      }}
    />
  </Box>
);

const defaultGetEntityId = entity => entity.id;

const ScrubberCell = ({ entity, rowIndex, field }) => {
  const { selectedIds, bulkState, toggleSelectedId, canRemove, getEntityId } = field._scrubberState;
  const rowNumber = rowIndex + 1;

  if (!canRemove) {
    return (
      <Box sx={styles.rowHeader}>
        <Typography sx={styles.rowNumber}>{rowNumber}</Typography>
      </Box>
    );
  }

  const entityId = getEntityId(entity);
  const isSelected = selectedIds.includes(entityId);

  return (
    <>
      {isSelected && <Box sx={styles.selectedRowBackground} />}
      <Box
        onClick={(e) => {
          e.stopPropagation();
          toggleSelectedId(entityId, e);
        }}
        sx={styles.rowClickTarget}
      >
        <Typography className="scrubber-row-number" sx={styles.hybridRowNumber(isSelected)}>
          {rowNumber}
        </Typography>
        <Checkbox
          className="scrubber-row-checkbox"
          checked={isSelected}
          disableRipple
          disableFocusRipple
          tabIndex={-1}
          sx={styles.hybridRowCheckbox(isSelected)}
          inputProps={{ "aria-label": `Select row ${rowNumber}` }}
        />
      </Box>
    </>
  );
};

const useTableBulkScrubber = (
  totalIds,
  {
    canRemove = true,
    getEntityId = defaultGetEntityId,
    orderedIds = totalIds,
    // Optional: () => Promise<ids>. When the caller does not preload every id
    // (see assetsCollection.fetchTotalIds), select-all fetches them on click.
    fetchTotalIds,
  } = {}
) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkState, setBulkState] = useState(false);
  const bulkStateRef = useRef(false);
  bulkStateRef.current = bulkState;

  const totalIdsRef = useRef(totalIds);
  totalIdsRef.current = totalIds;
  const orderedIdsRef = useRef(orderedIds);
  orderedIdsRef.current = orderedIds;
  const fetchTotalIdsRef = useRef(fetchTotalIds);
  fetchTotalIdsRef.current = fetchTotalIds;
  const lastSelectedIdRef = useRef(null);
  const selectAllRequestRef = useRef(0);

  const toggleBulkState = useCallback(() => {
    const newState = !bulkStateRef.current;
    lastSelectedIdRef.current = null;
    setBulkState(newState);
    selectAllRequestRef.current += 1;

    if (!newState) {
      setSelectedIds([]);
      return;
    }

    const knownIds = totalIdsRef.current || [];
    if (knownIds.length > 0 || !fetchTotalIdsRef.current) {
      setSelectedIds(knownIds);
      return;
    }

    const request = selectAllRequestRef.current;
    fetchTotalIdsRef.current()
      .then((ids) => {
        if (request !== selectAllRequestRef.current || !bulkStateRef.current) return;
        setSelectedIds(ids || []);
      })
      .catch(() => {
        if (request !== selectAllRequestRef.current) return;
        setBulkState(false);
        setSelectedIds([]);
      });
  }, []);

  const toggleSelectedId = useCallback((entityId, event) => {
    const isShift = event && event.shiftKey;
    const rangeAnchorId = lastSelectedIdRef.current;
    lastSelectedIdRef.current = entityId;

    setSelectedIds((prev) => {
      let newSelectedIds;

      if (isShift && rangeAnchorId) {
        const ids = orderedIdsRef.current;
        const start = ids.indexOf(rangeAnchorId);
        const end = ids.indexOf(entityId);

        if (start !== -1 && end !== -1) {
          const min = Math.min(start, end);
          const max = Math.max(start, end);
          const range = ids.slice(min, max + 1);
          const selectedSet = new Set(prev);
          range.forEach((id) => selectedSet.add(id));
          newSelectedIds = Array.from(selectedSet);
        }
      }

      if (!newSelectedIds) {
        newSelectedIds = prev.includes(entityId)
          ? prev.filter((id) => id !== entityId)
          : [...prev, entityId];
      }

      setBulkState(false);
      return newSelectedIds;
    });
  }, []);

  const label = useMemo(
    () => (
      <Tooltip title="Select All" placement="bottom" arrow>
        <Box sx={{ position: "absolute", inset: 0 }}>
          <SelectAllCell />
          {canRemove && (
            <Checkbox
              checked={bulkState}
              onClick={(e) => {
                e.stopPropagation();
                toggleBulkState();
              }}
              disableRipple
              disableFocusRipple
              sx={styles.invisibleCellCheckbox}
              inputProps={{ "aria-label": "Select all" }}
            />
          )}
        </Box>
      </Tooltip>
    ),
    [bulkState, canRemove, toggleBulkState]
  );

  const unSelectBulk = useCallback(() => {
    setBulkState(false);
    setSelectedIds([]);
  }, []);

  const scrubberState = useMemo(
    () => ({ selectedIds, bulkState, toggleSelectedId, canRemove, getEntityId }),
    [selectedIds, bulkState, toggleSelectedId, canRemove, getEntityId]
  );

  return {
    selectedIds,
    unSelectBulk,
    props: {
      label,
      component: ScrubberCell,
      _scrubberState: scrubberState,
    },
  };
};

export default useTableBulkScrubber;
